/**
 * useDashboardPage — generic hook for the per-page aggregator endpoints.
 *
 *   const { data, loading, error, reload } = useDashboardPage('money');
 *
 * Hits /api/v1/dashboard/<slug>. NO mock fallback per CEO directive
 * 2026-04-28 — null + error on failure, never fake numbers.
 */
import { useEffect, useState, useCallback, useRef } from "react";

/**
 * Dispatch a global "auth expired" event so the top-level App can clear
 * tokens and bounce the user to LoginScreen. Listened to in App.js.
 */
function fireAuthExpired() {
  try { window.dispatchEvent(new CustomEvent("fc:auth-expired")); } catch (_) {}
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
// Several aggregator endpoints (Creators / Audit / Acquisition) regularly take
// 5-9s on a cold cache. 8s was too tight — bumping to 20s so a real-but-slow
// query no longer trips the timeout and triggers a wasteful retry.
const FETCH_TIMEOUT_MS = 20000;

class TimeoutError extends Error {
  constructor(ms) { super(`timeout after ${ms}ms`); this.name = "TimeoutError"; }
}

function withTimeout(promise, ms, controller) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new TimeoutError(ms));
    }, ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); },
    );
  });
}

export function useDashboardPage(slug, range = null, rangeId = null, refreshKey = 0) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);
  // Hold the active controller so unmount + dep-change can abort the in-flight
  // request. Without this we leak setState calls on dead components and
  // surface stale data after the user has navigated away.
  const activeController = useRef(null);
  const mountedRef = useRef(true);
  // CRITICAL: must set TRUE on every mount AND false only on the final
  // unmount. React StrictMode (CRA dev) does mount → unmount → remount and
  // reuses ref cells across that cycle. If we only set false on cleanup,
  // the ref stays false on the second mount and `setLoading(false)` in the
  // finally block below gets skipped — page loads forever.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Stable serialization of the range so the effect doesn't loop on object identity.
  const rangeKey = range ? `${range.from || ''}|${range.to || ''}|${rangeId || ''}` : '';

  // Single fetch attempt. Returns the payload on success, throws on failure.
  // Kept separate so `load()` below can call it twice (silent retry) without
  // duplicating fetch wiring.
  const fetchOnce = useCallback(async (controller) => {
    const token  = (typeof localStorage !== "undefined") ? localStorage.getItem("fc_token") : null;
    const apiKey = process.env.REACT_APP_API_KEY;
    const headers = {};
    if (token)       headers.Authorization = `Bearer ${token}`;
    else if (apiKey) headers["X-API-Key"]  = apiKey;
    const params = new URLSearchParams();
    if (range && range.from) params.set('from', range.from);
    if (range && range.to)   params.set('to',   range.to);
    if (rangeId) params.set('rangeId', rangeId);
    const qs = params.toString();
    const url = `${API_BASE}/dashboard/${slug}${qs ? `?${qs}` : ''}`;
    const res = await withTimeout(
      fetch(url, { headers, signal: controller.signal }),
      FETCH_TIMEOUT_MS, controller,
    );
    if (res.status === 401) { fireAuthExpired(); throw new Error("HTTP 401"); }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    if (!json || !json.success || !json.data) throw new Error("malformed response envelope");
    return json.data;
  }, [slug, rangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (activeController.current) {
      try { activeController.current.abort(); } catch (_) {}
    }
    const controller = new AbortController();
    activeController.current = controller;
    setLoading(true); setError(null);
    try {
      let payload;
      try {
        payload = await fetchOnce(controller);
      } catch (firstErr) {
        // Bail straight away on aborts and auth failures — no point retrying.
        if (firstErr?.name === "AbortError") throw firstErr;
        if (/HTTP 401/.test(firstErr.message)) throw firstErr;
        // Skip retry on timeout: if the backend was too slow once it's almost
        // certainly going to be slow again, and forcing a second 20s wait
        // makes a slow page feel completely broken. Surface the error instead
        // so the user can hit Retry consciously.
        if (firstErr?.name === "TimeoutError") throw firstErr;
        if (!mountedRef.current) throw firstErr;
        // Silent retry after a brief backoff for transient errors only —
        // ECONNRESET, 502/503/504, malformed-envelope. Rescues the user
        // from a scary error card without making them click Retry themselves.
        await new Promise(r => setTimeout(r, 900));
        if (!mountedRef.current || controller.signal.aborted) throw firstErr;
        payload = await fetchOnce(controller);
      }
      if (!mountedRef.current) return;
      setData(payload);
    } catch (e) {
      if (e?.name === "AbortError") return;
      if (!mountedRef.current) return;
      setError(e.message || String(e));
      setData(null);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fetchOnce]);

  useEffect(() => {
    load();
    return () => {
      // Abort the in-flight request when the component unmounts OR deps change.
      if (activeController.current) {
        try { activeController.current.abort(); } catch (_) {}
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [refreshKey, slug, rangeKey]);

  return { data, error, loading, reload: load };
}

// ─── Shared formatters ──────────────────────────────────────────
export function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
export function formatNum(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}
export function formatPct(n, dp = 1) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(dp) + "%";
}
export function formatSeconds(n) {
  if (n == null) return "—";
  const s = Number(n);
  if (!Number.isFinite(s)) return "—";
  if (s < 60) return s.toFixed(0) + "s";
  return Math.floor(s / 60) + "m " + Math.round(s % 60) + "s";
}
